import * as React from "react";
import {FC, useRef} from "react";
import {Box, DialogActions, DialogContent, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {AnnouncementAuthorComponent} from "@/pages/HomePage/AnnouncementsSection/AnnouncementCard";
import {AnnouncementPageContent} from "@/pages/ModulePage/AnnouncementPage/AnnouncementPageContent";
import {getAnnouncementAuthorDisplayDate} from "@/app2/pages/ManagerView/Pages/Announcements/announcementAdminStatus";
import {IAnnouncement} from "@/types/announcement";
import {IUserEntity} from "@buildwitt/shapeshift-types/entities";
import {WYSIWYG} from "@buildwitt/shapeshift-common/v1/wysiwyg";
import {Files} from "@buildwitt/shapeshift-common/v1/files";
import {Links} from "@buildwitt/shapeshift-common/v1/links";

const useStyles = makeStyles()((theme) => ({
	title: {
		marginTop: "8px",
		marginBottom: "8px",
		fontSize: "34px"
	},
	content: {
		marginTop: "8px",
		marginBottom: "8px",
		fontSize: "16px",
		color: "rgba(249, 249, 249, 0.7)",
		fontWeight: 400
	},
	richContent: {
		marginTop: "8px",
		marginBottom: "16px",
		fontSize: "16px",
		color: "rgba(249, 249, 249, 0.7)",
		"& p": {lineHeight: 1.5},
		"& a": {
			textDecoration: "underline",
			color: theme.palette.primary.main
		}
	},
	attachmentsSectionTitle: {
		color: "#FFFFFF",
		fontFamily: "Titillium Web, sans-serif",
		fontWeight: 600,
		fontSize: "20px",
		lineHeight: "150%",
		letterSpacing: "0.15px",
		marginTop: "16px",
		marginBottom: "8px"
	},
	attachmentsFiles: {
		marginBottom: "16px",
		"& > div": {
			display: "flex",
			flexWrap: "wrap",
			alignItems: "center",
			background: "transparent",
			padding: "12px 0",
			marginBottom: "8px",
			gap: "8px",
			position: "relative",
			paddingLeft: "22px"
		},
		"& > div::before": {
			content: '""',
			position: "absolute",
			left: 0,
			top: "50%",
			transform: "translateY(-50%)",
			width: "18px",
			height: "18px",
			flexShrink: 0,
			backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
				`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="${theme.palette.primary.main}" d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5C7 20.54 9.46 23 12.5 23s5.5-2.46 5.5-5.5V6h-1.5z"/></svg>`
			)}")`,
			backgroundRepeat: "no-repeat",
			backgroundPosition: "center",
			backgroundSize: "contain"
		},
		"& > div > div:first-of-type": {
			display: "none"
		},
		"& > div > div:nth-of-type(2)": {
			margin: "0 !important",
			flexGrow: 1,
			minWidth: 0
		},
		"& > div > div:nth-of-type(2) h6": {
			fontFamily: "Titillium Web, sans-serif",
			fontWeight: 400,
			fontSize: "16px",
			lineHeight: "140%",
			letterSpacing: "0.15px",
			textDecoration: "underline",
			textDecorationStyle: "solid",
			color: theme.palette.primary.main,
			margin: 0
		},
		"& > div > a": {
			position: "absolute",
			left: 0,
			top: 0,
			right: 0,
			bottom: 0,
			zIndex: 1,
			border: "none",
			padding: 0,
			background: "transparent"
		},
		"& > div > a > div": {
			visibility: "hidden"
		}
	},
	attachmentsLinks: {
		marginBottom: "16px",
		"& a": {
			fontFamily: "Titillium Web, sans-serif",
			fontWeight: 400,
			fontSize: "16px",
			lineHeight: "140%",
			letterSpacing: "0.15px",
			textDecoration: "underline",
			textDecorationStyle: "solid",
			textDecorationSkipInk: "auto",
			color: theme.palette.primary.main
		}
	},
	dialogContentRoot: {
		padding: "5px",
		textAlign: "left"
	},
	dialogActionsRoot: {
		justifyContent: "flex-start"
	}
}));

export interface IAnnouncementPreviewProps {
	announcement: IAnnouncement;
	author: IUserEntity;
}

export const AnnouncementPreview: FC<IAnnouncementPreviewProps> = ({announcement, author}) => {
	const {classes} = useStyles();
	const videoContainerRef = useRef<HTMLDivElement | null>(null);

	const rawFiles = announcement?.files;
	const filesList = Array.isArray(rawFiles) ? rawFiles : rawFiles ? [rawFiles] : [];

	const rawLinks = announcement?.links;
	let linksList: string[] = [];
	if (rawLinks != null) {
		if (Array.isArray(rawLinks)) {
			linksList = rawLinks
				.map((l: unknown) => (typeof l === "string" ? l : (l as {url?: string})?.url ?? ""))
				.filter(Boolean);
		} else if (typeof rawLinks === "string") {
			linksList = [rawLinks];
		}
	}

	return (
		<>
			<Box
				ref={videoContainerRef}
				aria-label="Announcement media content"
				data-test-id="announcement-media-container"
			>
				{announcement?.videoUrl && (
					<AnnouncementPageContent announcement={announcement} isForHomePage={true} />
				)}
			</Box>

			<DialogContent
				className={classes.dialogContentRoot}
				aria-label="Announcement content"
				data-test-id="announcement-modal-content"
			>
				<Typography variant="h4" className={classes.title} data-test-id="announcement-title">
					{announcement?.title}
				</Typography>

				<Typography className={classes.content} variant={"body1"} data-test-id="announcement-subtitle">
					{announcement?.subtitle}
				</Typography>

				{announcement?.content && (
					<WYSIWYG className={classes.richContent} data-test-id="announcement-rich-content">
						{announcement.content}
					</WYSIWYG>
				)}

				{filesList.length > 0 && (
					<>
						<Typography className={classes.attachmentsSectionTitle} variant="subtitle1">
							Files
						</Typography>
						<Files className={classes.attachmentsFiles} files={filesList} />
					</>
				)}

				{linksList.length > 0 && (
					<>
						<Typography className={classes.attachmentsSectionTitle} variant="subtitle1">
							Links
						</Typography>
						<Links className={classes.attachmentsLinks} links={linksList} />
					</>
				)}
			</DialogContent>

			<DialogActions
				className={classes.dialogActionsRoot}
				aria-label="Announcement author information"
				data-test-id="announcement-modal-footer"
			>
				<AnnouncementAuthorComponent
					author={author}
					date={getAnnouncementAuthorDisplayDate({
						publishedAt: announcement?.publishedAt,
						scheduledDeliveryDate: announcement?.scheduledDeliveryDate
					})}
				/>
			</DialogActions>
		</>
	);
};
